import isEqual from 'lodash/isEqual';

import {
  getUniqueValues,
  isResidentRole,
  isStaffRole,
  loadServiceData,
  toCamelCaseWithSpaces,
  toSentenceCase,
} from '../../utils';
import { getAllPersonRoles, getFullName, getRoleName } from '../Person/utils';

export const getPersonRolesInfo = (roles) => {
  if (!roles || !roles.length) return '';
  const allRoles = loadServiceData().role || [];
  const reducer = (accumulator, currentValue) => {
    const { roleId, role, propertyLevel } = currentValue;
    const currentRole = allRoles.find(({ id }) => id === roleId || id === role);
    const roleName = currentRole && toSentenceCase(currentRole.name);
    const propertyName = currentValue[`${propertyLevel}Name`];
    if (!roleName) return accumulator;
    if (!propertyName) return `${accumulator} | ${roleName}`;
    return `${accumulator} | ${roleName} in ${propertyName}`;
  };
  return roles.reduce(reducer, '');
};

export const formatPersonSelect = (person, roleType, pmcId) => {
  if (!person) return null;

  const { firstName, lastName, id, roles } = person;
  if (firstName && lastName && id && !roles) {
    return {
      label: `${firstName} ${lastName}`,
      value: id,
    };
  }

  const activeRoles = roles?.filter(({ active }) => active);
  const residentRoles = activeRoles?.filter(({ roleId }) =>
    isResidentRole(roleId),
  );
  const staffRoles = activeRoles?.filter(({ roleId }) => isStaffRole(roleId));
  const personRoles = {
    residentRoles,
    staffRoles,
  };
  // if there is a pmcId we interested only in roles in a property
  if (pmcId && personRoles?.[`${roleType}Roles`]?.length) {
    personRoles[`${roleType}Roles`] = personRoles[`${roleType}Roles`].filter(
      (role) => Number(role?.pmcId) === Number(pmcId),
    );
  }
  if (!personRoles[`${roleType}Roles`].length) return null;
  const rolesInfo = getPersonRolesInfo(personRoles[`${roleType}Roles`]);
  return {
    label: `${firstName} ${lastName} ${rolesInfo}`,
    value: pmcId ? personRoles[`${roleType}Roles`]?.[0]?.id : id,
  };
};

export const formatSelectOptions = (options, roleType, pmcId) => {
  const results = options && options.data;
  if (!results || !results.length) return [];
  return results
    .map((person) => formatPersonSelect(person, roleType, pmcId))
    .filter((value) => value);
};

export const getPersonProperties = (id, persons) => {
  if (!persons?.length > 0) return [];

  const person = persons?.find((person) => person?.id === id);
  const staffRoles = person?.staffRoles || [];
  const residentRoles = person?.residentRoles || [];
  const roles = [...staffRoles, ...residentRoles];
  if (!roles?.length) return [];
  const roleOptions = {};
  roles.forEach((role) => {
    const { propertyId, propertyName, role: roleId } = role;

    const roleName = toSentenceCase(getRoleName(roleId));
    const previousRoles = roleOptions[propertyId]?.roleNames ?? [];

    roleOptions[propertyId] = {
      propertyId,
      propertyName,
      roleNames: [...new Set([...previousRoles, roleName])],
    };
  });
  return Object.values(roleOptions).map(
    ({ propertyId, propertyName, roleNames }) => ({
      label: `${propertyName} | ${roleNames.join(', ')}`,
      value: propertyId,
    }),
  );
};

export const getResidentProperties = (person) => {
  const roles = person?.residentRoles;
  if (!roles) return [];
  const formattedRoles = roles.map(({ propertyId, propertyName }) => ({
    label: propertyName,
    value: propertyId,
  }));
  return getUniqueValues(formattedRoles, 'value');
};

export const isPersonOrPropertyChanged = (
  currentValues = {},
  initialValues = {},
  isCreate,
) => {
  if (isCreate) return null;
  const { person: currentPerson, properties: currentProperties } =
    currentValues;
  const { person: initialPerson, properties: initialProperties } =
    initialValues;
  if (
    !isEqual(currentPerson, initialPerson) ||
    !isEqual(currentProperties, initialProperties)
  ) {
    return true;
  }
  return null;
};

export const getPersonOptions = (persons) => {
  if (!persons?.length) return [];
  return persons.map((person) => {
    const { id: value } = person;
    const roles = getAllPersonRoles(person)
      .map(({ role }) => toCamelCaseWithSpaces(getRoleName(role)))
      .join(' | ');
    const label = roles
      ? `${getFullName(person)} | ${roles}`
      : getFullName(person);
    return {
      value,
      label,
    };
  });
};

export const getPMCsFromBuildings = (buildings) =>
  buildings?.reduce((pmcs, building) => {
    const pmc = building?.parent;
    const pmcId = pmc?.id;
    if (!pmcs.find((pmc) => pmc.id === pmcId)) pmcs.push(pmc);
    return pmcs;
  }, []);
